/**
 * LESEMODUS — ÉN MÅTE FOR HELE APPEN (grep D1, QA13 eksempel 22 og punkt 110).
 *
 * Atle: «Har man ikke rettigheten, får man ikke åpnet redigeringen. Dette er et prinsipp for hele
 * appen.» API-et legger `viewer` på hver rad som kan redigeres, regnet av den samme `can()` med den
 * fulle aktøren. Denne fila er den ene måten komponentene leser det på:
 *
 *   - `RowViewerProvider` rundt en skuff, et panel eller et skjema. `Modal` gjør det selv når den
 *     får `viewer`.
 *   - `Field` (og kontrollene som tegner sin egen `Field`) spør `useFieldLock(name)`: uten
 *     `canEdit` er hvert felt skrivebeskyttet; et felt i `lockedFields` er det alene, og grunnen
 *     står under feltet.
 *   - `useCanEdit()` og `<EditOnly>` for knappene som ikke er felt — «Rediger», «Legg til»,
 *     søppelkassa. Uten `canEdit` tegnes de ikke i det hele tatt, de gråes ikke.
 *
 * UTEN EN TILBYDER ER ALT SOM FØR. En side som ikke har tatt `viewer` i bruk ennå, får ingen
 * endring — det er det som lar bølge C ta det i bruk side for side.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { lockedField, type LockedFieldReason, type RowViewer } from '@tvitvi/shared';

import type { TKey } from '@/i18n/types';

const RowViewerContext = createContext<RowViewer | null>(null);

/** Grunnen som setning. En tabell og ikke en template-nøkkel: en ny grunn uten tekst er en typefeil. */
const REASON: Readonly<Record<LockedFieldReason, TKey>> = {
  owned_by_person: 'viewer.locked.ownedByPerson',
  login_email: 'viewer.locked.loginEmail',
  owned_by_other_organization: 'viewer.locked.ownedByOtherOrganization',
  organiser_only: 'viewer.locked.organiserOnly',
  no_permission: 'viewer.locked.noPermission',
};

/** Den samme grunnen når API-et sa hvem som eier feltet («Kapabel fører dette»). */
const REASON_BY: Readonly<Record<LockedFieldReason, TKey>> = {
  owned_by_person: 'viewer.lockedBy.ownedByPerson',
  login_email: 'viewer.locked.loginEmail',
  owned_by_other_organization: 'viewer.lockedBy.ownedByOtherOrganization',
  organiser_only: 'viewer.lockedBy.organiserOnly',
  no_permission: 'viewer.locked.noPermission',
};

export interface RowViewerProviderProps {
  /** `null`/`undefined` betyr «ingen svar fra API-et» — da gjelder det som gjaldt før. */
  viewer: RowViewer | null | undefined;
  children: ReactNode;
}

export function RowViewerProvider({ viewer, children }: RowViewerProviderProps): ReactNode {
  return <RowViewerContext.Provider value={viewer ?? null}>{children}</RowViewerContext.Provider>;
}

/** Svaret fra den nærmeste tilbyderen, eller null. */
export function useRowViewer(): RowViewer | null {
  return useContext(RowViewerContext);
}

/** Kan leseren endre raden? Uten tilbyder: ja — rollesjekken kalleren alt har gjort gjelder. */
export function useCanEdit(): boolean {
  return useRowViewer()?.canEdit ?? true;
}

export interface FieldLock {
  readonly readOnly: boolean;
  /** Setningen som forklarer hvorfor, når ett felt er låst for seg. Null for hele raden. */
  readonly reason: string | null;
}

const OPEN: FieldLock = { readOnly: false, reason: null };

/**
 * Er feltet `name` låst for leseren? Hele raden låst gir skrivebeskyttet uten egen grunn — skuffen
 * er en visning, og en setning under hvert felt ville bare vært støy.
 */
export function useFieldLock(name?: string): FieldLock {
  const { t } = useTranslation();
  const viewer = useRowViewer();
  if (viewer === null) return OPEN;
  if (!viewer.canEdit) return { readOnly: true, reason: null };
  if (name === undefined) return OPEN;
  const lock = lockedField(viewer, name);
  if (lock === null) return OPEN;
  return {
    readOnly: true,
    reason:
      lock.by === null || lock.by === undefined
        ? t(REASON[lock.reason])
        : t(REASON_BY[lock.reason], { by: lock.by }),
  };
}

/** Tegner barna bare når leseren kan endre raden. */
export function EditOnly({ children }: { children: ReactNode }): ReactNode {
  return useCanEdit() ? children : null;
}

/**
 * Det samme svaret for en celle i hurtigredigering: `false` når raden er låst, grunnen (som nøkkel)
 * når akkurat feltet er det, ellers `fallback` — rollesjekken siden alt gjør. Uten `viewer` fra
 * API-et er det bare `fallback`.
 */
export function viewerEditPermission(
  viewer: RowViewer | null | undefined,
  field: string,
  fallback: boolean,
): boolean | TKey {
  if (viewer === null || viewer === undefined) return fallback;
  if (!viewer.canEdit) return false;
  const lock = lockedField(viewer, field);
  return lock === null ? fallback : REASON[lock.reason];
}
